import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '@/lib/api/apiClient';
import { AxiosResponse } from 'axios';
import { ListInvestmentsResponse } from '@inversor-hub/types/src/api/investments/list';
import {
  AskInvestmentAssistantRequestBody,
  AskInvestmentAssistantResponse,
  InvestmentDTO,
  UserRole,
  MediaType,
  ListInvestmentMediaResponse,
  CreateCoInvestmentRequestBody,
  CreateCoInvestmentResponse,
  CoInvestorListResponse,
  AcceptCoInvestmentInvitationRequestBody,
  AcceptInvestmentInvitationRequestBody,
  InviteInvestmentInvestorRequestBody,
  UpdateCoInvestorPaidStatusRequestBody,
  UpdateCoInvestorsRequestBody,
  MyCoInvestorContractResponse,
} from '@inversor-hub/types';
import { UpdateRequestBody } from '@inversor-hub/types/src/api/investments/update-request';
import {
  CreateManualInvestmentRequestBody,
  CreateManualInvestmentResponse,
} from '@inversor-hub/types/src/api/investments/manual-create-request';

export const LIST_INVESTMENTS_KEY = 'investments';
export const LIST_INVESTMENT_MEDIA_KEY = 'investment-media';

export function useListInvestments<R extends UserRole>(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: [LIST_INVESTMENTS_KEY],
    queryFn: async () => {
      return apiClient.get<ListInvestmentsResponse<R>>(`/investments`);
    },
    select: (response) => response.data.data,
    enabled: options?.enabled,
  });
}

export function useGetInvestment(investmentId?: string) {
  return useQuery({
    queryKey: ['investment', investmentId],
    queryFn: async () => {
      return apiClient.get<{
        data: InvestmentDTO;
      }>(`/investments/${investmentId}`);
    },
    select: (response) => response.data.data,
    enabled: !!investmentId,
  });
}

export function useUpdateInvestment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      investmentId,
      data,
    }: {
      investmentId: string;
      data: UpdateRequestBody;
    }) => {
      const response = await apiClient.patch<
        unknown,
        AxiosResponse<{ data: InvestmentDTO }>,
        UpdateRequestBody
      >(`/investments/${investmentId}`, data);
      return response.data.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [LIST_INVESTMENTS_KEY] });
      queryClient.invalidateQueries({ queryKey: ['investment', variables.investmentId] });
      queryClient.invalidateQueries({
        queryKey: ['investment-rental-periods', variables.investmentId],
      });
    },
  });
}

export function useCreateManualInvestment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateManualInvestmentRequestBody) => {
      const response = await apiClient.post<
        unknown,
        AxiosResponse<CreateManualInvestmentResponse>,
        CreateManualInvestmentRequestBody
      >('/investments/manual', data);
      return response.data.data;
    },
    onSuccess: (investment) => {
      queryClient.invalidateQueries({ queryKey: [LIST_INVESTMENTS_KEY] });
      queryClient.invalidateQueries({ queryKey: ['investment-rental-periods', investment.id] });
    },
  });
}

export function useUploadInvestmentMainImage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ investmentId, file }: { investmentId: string; file: File }) => {
      const formData = new FormData();
      formData.append('file', file);

      const response = await apiClient.post<{ data: InvestmentDTO }>(
        `/investments/${investmentId}/main-image`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
        }
      );

      return response.data.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [LIST_INVESTMENTS_KEY] });
      queryClient.invalidateQueries({ queryKey: ['investment', variables.investmentId] });
    },
  });
}

export function useDeleteInvestmentMainImage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (investmentId: string) => {
      const response = await apiClient.delete<{ data: InvestmentDTO }>(
        `/investments/${investmentId}/main-image`
      );
      return response.data.data;
    },
    onSuccess: (_, investmentId) => {
      queryClient.invalidateQueries({ queryKey: [LIST_INVESTMENTS_KEY] });
      queryClient.invalidateQueries({ queryKey: ['investment', investmentId] });
    },
  });
}

export function useGetInvestmentMedia(investmentId?: string) {
  return useQuery({
    queryKey: [LIST_INVESTMENT_MEDIA_KEY, investmentId],
    queryFn: async () => {
      return apiClient.get<ListInvestmentMediaResponse>(`/investments/${investmentId}/media`);
    },
    select: (response) => response.data.data,
    enabled: !!investmentId,
  });
}

export function useUploadInvestmentMedia() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      investmentId,
      file,
      fileType,
    }: {
      investmentId: string;
      file: File;
      fileType: MediaType;
    }) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('fileType', fileType);

      await apiClient.post(`/investments/${investmentId}/media`, formData);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [LIST_INVESTMENT_MEDIA_KEY, variables.investmentId],
      });
    },
  });
}

export function useDeleteInvestmentMedia() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/investment-media/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [LIST_INVESTMENT_MEDIA_KEY],
      });
    },
  });
}

export function useAskInvestmentAssistant(investmentId: string) {
  return useMutation({
    mutationFn: async (payload: AskInvestmentAssistantRequestBody) => {
      const response = await apiClient.post<AskInvestmentAssistantResponse>(
        `/investments/${investmentId}/assistant`,
        payload
      );
      return response.data.data.reply;
    },
  });
}

// Co-investment hooks
export const CO_INVESTORS_KEY = 'co-investors';
export const MY_CO_INVESTOR_CONTRACT_KEY = 'my-co-investor-contract';

export function useCreateCoInvestment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateCoInvestmentRequestBody) => {
      const response = await apiClient.post<
        unknown,
        AxiosResponse<CreateCoInvestmentResponse>,
        CreateCoInvestmentRequestBody
      >('/co-investments', data);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [LIST_INVESTMENTS_KEY] });
    },
  });
}

export function useGetCoInvestors(investmentId?: string) {
  return useQuery({
    queryKey: [CO_INVESTORS_KEY, investmentId],
    queryFn: async () => {
      return apiClient.get<CoInvestorListResponse>(`/investments/${investmentId}/co-investors`);
    },
    select: (response) => response.data.data,
    enabled: !!investmentId,
  });
}

export function useUpdateCoInvestors() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      investmentId,
      data,
    }: {
      investmentId: string;
      data: UpdateCoInvestorsRequestBody;
    }) => {
      await apiClient.put(`/investments/${investmentId}/co-investors`, data);
    },
    onSuccess: (_, { investmentId }) => {
      queryClient.invalidateQueries({ queryKey: [CO_INVESTORS_KEY, investmentId] });
      queryClient.invalidateQueries({ queryKey: ['investment', investmentId] });
      queryClient.invalidateQueries({ queryKey: [LIST_INVESTMENTS_KEY] });
      queryClient.invalidateQueries({ queryKey: [MY_CO_INVESTOR_CONTRACT_KEY, investmentId] });
    },
  });
}

export function useResendCoInvestorInvitation() {
  return useMutation({
    mutationFn: async ({
      investmentId,
      coInvestorId,
    }: {
      investmentId: string;
      coInvestorId: string;
    }) => {
      await apiClient.post(`/investments/${investmentId}/co-investors/${coInvestorId}/resend`);
    },
  });
}

export function useUpdateCoInvestorPaidStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      investmentId,
      coInvestorId,
      data,
    }: {
      investmentId: string;
      coInvestorId: string;
      data: UpdateCoInvestorPaidStatusRequestBody;
    }) => {
      const response = await apiClient.patch<
        unknown,
        AxiosResponse<{ data: CoInvestorListResponse['data'][number] }>,
        UpdateCoInvestorPaidStatusRequestBody
      >(`/investments/${investmentId}/co-investors/${coInvestorId}/paid-status`, data);
      return response.data.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [CO_INVESTORS_KEY, variables.investmentId] });
    },
  });
}

export function useAcceptCoInvestmentInvitation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: AcceptCoInvestmentInvitationRequestBody) => {
      await apiClient.post('/co-investments/accept-invitation', data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [LIST_INVESTMENTS_KEY] });
    },
  });
}

export function useAcceptInvestmentInvitation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: AcceptInvestmentInvitationRequestBody) => {
      await apiClient.post('/investments/accept-invitation', data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [LIST_INVESTMENTS_KEY] });
    },
  });
}

export function useInviteInvestmentInvestor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      investmentId,
      data,
    }: {
      investmentId: string;
      data: InviteInvestmentInvestorRequestBody;
    }) => {
      await apiClient.post(`/investments/${investmentId}/investor-invitation`, data);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [LIST_INVESTMENTS_KEY] });
      queryClient.invalidateQueries({ queryKey: ['investment', variables.investmentId] });
    },
  });
}

export function useResendInvestmentInvestorInvitation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (investmentId: string) => {
      await apiClient.post(`/investments/${investmentId}/investor-invitation/resend`);
    },
    onSuccess: (_, investmentId) => {
      queryClient.invalidateQueries({ queryKey: [LIST_INVESTMENTS_KEY] });
      queryClient.invalidateQueries({ queryKey: ['investment', investmentId] });
    },
  });
}

export function useGenerateCoInvestorContract() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      investmentId,
      coInvestorId,
    }: {
      investmentId: string;
      coInvestorId: string;
    }) => {
      const response = await apiClient.post<{ data: { url: string } }>(
        `/investments/${investmentId}/co-investors/${coInvestorId}/contract`
      );
      return response.data.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: [CO_INVESTORS_KEY, variables.investmentId] });
    },
  });
}

export function useGetCoInvestorContractUrl() {
  return useMutation({
    mutationFn: async ({
      investmentId,
      coInvestorId,
    }: {
      investmentId: string;
      coInvestorId: string;
    }) => {
      const response = await apiClient.get<{ data: { url: string } }>(
        `/investments/${investmentId}/co-investors/${coInvestorId}/contract/url`
      );
      return response.data.data;
    },
  });
}

export function useGetMyCoInvestorContract(investmentId?: string) {
  return useQuery({
    queryKey: [MY_CO_INVESTOR_CONTRACT_KEY, investmentId],
    queryFn: async () => {
      const response = await apiClient.get<MyCoInvestorContractResponse>(
        `/investments/${investmentId}/co-investor-contract`
      );
      return response.data.data;
    },
    enabled: !!investmentId,
  });
}

export function useSignMyCoInvestorContract() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ investmentId }: { investmentId: string }) => {
      await apiClient.post(`/investments/${investmentId}/co-investor-contract/sign`);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: [CO_INVESTORS_KEY, variables.investmentId] });
      queryClient.invalidateQueries({
        queryKey: [MY_CO_INVESTOR_CONTRACT_KEY, variables.investmentId],
      });
    },
  });
}
